// Firebase Service - Initializes Firebase and exposes it globally
// This ensures Firebase is initialized once and available to all scripts

import { initializeApp, FirebaseApp } from 'firebase/app';
import { 
    getFirestore, 
    Firestore, 
    collection, 
    getDocs, 
    doc, 
    getDoc,
    query,
    where,
    orderBy 
} from 'firebase/firestore';
import { getStorage, FirebaseStorage } from 'firebase/storage';
import { Credentials } from '../assets/features/Credentials';
import { FirestorePathService } from './FirestorePathService';

let firebaseApp: FirebaseApp | null = null;
let firestoreDb: Firestore | null = null;
let firebaseStorage: FirebaseStorage | null = null;

// Validate Firebase credentials
function validateFirebaseCredentials(): void {
    const requiredCredentials = {
        'FIREBASE_API_KEY': Credentials.FIREBASE_API_KEY,
        'FIREBASE_AUTH_DOMAIN': Credentials.FIREBASE_AUTH_DOMAIN,
        'FIREBASE_PROJECT_ID': Credentials.FIREBASE_PROJECT_ID,
        'FIREBASE_STORAGE_BUCKET': Credentials.FIREBASE_STORAGE_BUCKET,
        'FIREBASE_MESSAGING_SENDER_ID': Credentials.FIREBASE_MESSAGING_SENDER_ID,
        'FIREBASE_APP_ID': Credentials.FIREBASE_APP_ID,
    };

    const missing = Object.entries(requiredCredentials)
        .filter(([_, value]) => !value || value === '')
        .map(([key]) => key);

    if (missing.length > 0) {
        const errorMessage = `
❌ Missing Firebase credentials: ${missing.join(', ')}

🔧 To fix this:

1. For LOCAL DEVELOPMENT:
   - Copy env.example.txt to .env in the project root
   - Fill in your Firebase credentials from:
     https://console.firebase.google.com/project/YOUR_PROJECT/settings/general
   
2. For PRODUCTION (Firebase Hosting):
   - Set environment variables in Firebase:
     firebase functions:config:set vite.firebase_api_key="YOUR_KEY"
   - Or add them during build with --env flag
   
3. Restart your dev server after creating .env file
        `.trim();
        
        console.error(errorMessage);
        throw new Error('Firebase credentials not configured');
    }
}

// Firebase configuration
const firebaseConfig = {
    apiKey: Credentials.FIREBASE_API_KEY,
    authDomain: Credentials.FIREBASE_AUTH_DOMAIN,
    projectId: Credentials.FIREBASE_PROJECT_ID,
    storageBucket: Credentials.FIREBASE_STORAGE_BUCKET,
    messagingSenderId: Credentials.FIREBASE_MESSAGING_SENDER_ID,
    appId: Credentials.FIREBASE_APP_ID,
    measurementId: Credentials.FIREBASE_MEASUREMENT_ID
};

/**
 * Initialize Firebase app and Firestore
 * This should be called once when the app starts
 */
export function initializeFirebase(): void {
    if (firebaseApp) {
        console.log('Firebase already initialized');
        return;
    }

    try {
        // Validate credentials first
        validateFirebaseCredentials();
        
        firebaseApp = initializeApp(firebaseConfig);
        firestoreDb = getFirestore(firebaseApp);
        firebaseStorage = getStorage(firebaseApp);
        
        // Expose Firebase to window for public scripts
        (window as any).firebaseApp = firebaseApp;
        (window as any).firebaseDb = firestoreDb;
        (window as any).firebaseStorage = firebaseStorage;
        (window as any).firebaseConfig = firebaseConfig;
        
        // Expose Firestore functions to window (for public scripts to use)
        (window as any).firestoreFunctions = {
            collection: (db: Firestore, path: string) => collection(db, path),
            getDocs: getDocs,
            doc: (db: Firestore, path: string, ...pathSegments: string[]) => doc(db, path, ...pathSegments),
            getDoc: getDoc,
            query: query,
            where: where,
            orderBy: orderBy
        };
        
        // Expose FirestorePathService for multi-tenant access
        (window as any).FirestorePathService = {
            getCollectionRef: (db: Firestore, collectionName: string) => FirestorePathService.getCollectionRef(db, collectionName),
            getDocRef: (db: Firestore, collectionName: string, docId: string) => FirestorePathService.getDocRef(db, collectionName, docId),
            getClientId: () => Credentials.CLIENT_ID
        };
        
        // Mark as initialized
        (window as any).firebaseInitialized = true;
        
        // Dispatch event to notify scripts that Firebase is ready
        window.dispatchEvent(new CustomEvent('firebaseReady', { 
            detail: { 
                app: firebaseApp, 
                db: firestoreDb,
                functions: (window as any).firestoreFunctions
            } 
        }));
        
        console.log('✅ Firebase initialized and exposed globally');
    } catch (error) {
        console.error('❌ Failed to initialize Firebase:', error);
        throw error;
    }
}

/**
 * Get Firebase app instance
 */
export function getFirebaseApp(): FirebaseApp {
    if (!firebaseApp) {
        throw new Error('Firebase not initialized. Call initializeFirebase() first.');
    }
    return firebaseApp;
}

/**
 * Get Firestore instance
 */
export function getFirestoreDb(): Firestore {
    if (!firestoreDb) {
        // Auto-initialize if not done yet (safety fallback)
        console.warn('⚠️ Firestore accessed before initialization. Auto-initializing...');
        try {
            initializeFirebase();
            if (!firestoreDb) {
                throw new Error('Firestore not initialized. Call initializeFirebase() first.');
            }
        } catch (error) {
            console.error('❌ Failed to auto-initialize Firebase:', error);
        throw new Error('Firestore not initialized. Call initializeFirebase() first.');
        }
    }
    return firestoreDb;
}

/**
 * Get Firebase Storage instance
 */
export function getFirebaseStorage(): FirebaseStorage {
    if (!firebaseStorage) {
        // Auto-initialize if not done yet (safety fallback)
        console.warn('⚠️ Storage accessed before initialization. Auto-initializing...');
        try {
            initializeFirebase();
            if (!firebaseStorage) {
                throw new Error('Storage not initialized. Call initializeFirebase() first.');
            }
        } catch (error) {
            console.error('❌ Failed to auto-initialize Firebase:', error);
            throw new Error('Storage not initialized. Call initializeFirebase() first.');
        }
    }
    return firebaseStorage;
}

/**
 * Check if Firebase is initialized
 */
export function isFirebaseInitialized(): boolean {
    return firebaseApp !== null && firestoreDb !== null;
}

/**
 * Get a collection reference using the multi-tenant structure
 * 
 * @param collectionName - Name of the collection (e.g., 'products', 'videos')
 * @returns Collection reference to clients/{clientId}/{collectionName}
 * 
 * @example
 * const productsRef = getClientCollection('products');
 * const snapshot = await getDocs(productsRef);
 */
export function getClientCollection(collectionName: string) {
    const db = getFirestoreDb();
    return FirestorePathService.getCollectionRef(db, collectionName);
}

/**
 * Get a document reference using the multi-tenant structure
 * 
 * @param collectionName - Name of the collection
 * @param docId - Document ID
 * @returns Document reference to clients/{clientId}/{collectionName}/{docId}
 * 
 * @example
 * const productRef = getClientDoc('products', 'product123');
 * const docSnap = await getDoc(productRef);
 */
export function getClientDoc(collectionName: string, docId: string) {
    const db = getFirestoreDb();
    return FirestorePathService.getDocRef(db, collectionName, docId);
}

/**
 * Get the current client ID
 */
export function getClientId(): string {
    return Credentials.CLIENT_ID;
}

export default {
    initializeFirebase,
    getFirebaseApp,
    getFirestoreDb,
    getFirebaseStorage,
    isFirebaseInitialized,
    getClientCollection,
    getClientDoc,
    getClientId
};

